import type { TripDay } from '../types';

export type DiscoverCategory = 'food' | 'essential' | 'practical' | 'alternative';

export interface DiscoverRecommendation {
  id: string;
  date: string;
  category: DiscoverCategory;
  title: string;
  description: string;
  context: string;
  practical: string;
  sourceLabel: string;
  sourceUrl: string;
  eventId?: string;
}

export const discoverCategoryLabels: Record<DiscoverCategory, string> = {
  food: 'Comer cerca',
  essential: 'Imperdible',
  practical: 'Práctico',
  alternative: 'Plan alterno',
};

export const discoverRecommendations: DiscoverRecommendation[] = [
  {
    id: 'segovia-alcazar',
    date: '2026-10-26',
    category: 'essential',
    title: 'Alcázar de Segovia',
    description: 'Castillo real medieval en el extremo del casco histórico, con vistas sobre los valles de Eresma y Clamores.',
    context: 'Encaja después del check-in en Hotel Don Felipe, a un ritmo tranquilo tras el vuelo nocturno.',
    practical: 'Revisa el último acceso y la modalidad de visita antes de salir.',
    sourceLabel: 'Turismo de Segovia',
    sourceUrl: 'https://www.turismodesegovia.com/en/monuments/alcazar-royal-residence',
    eventId: 'don-felipe-in',
  },
  {
    id: 'madrid-retiro',
    date: '2026-10-28',
    category: 'essential',
    title: 'Paseo por El Retiro',
    description: 'Parque histórico junto al Paisaje de la Luz; una opción flexible para el día abierto en Madrid.',
    context: 'Es el plan principal del día: permite ajustar duración, pausas y comida sin interferir con reservas.',
    practical: 'En octubre el horario publicado es 06:00–22:00; consulta avisos meteorológicos municipales antes de entrar.',
    sourceLabel: 'Turismo Madrid',
    sourceUrl: 'https://www.esmadrid.com/informacion-tur%C3%ADstica/parque-del-retiro',
  },
  {
    id: 'barcelona-gothic',
    date: '2026-10-29',
    category: 'alternative',
    title: 'Primer paseo por el Gòtic',
    description: 'Recorrido corto a pie por el entorno del alojamiento en Ciutat Vella, sin necesidad de sumar una reserva.',
    context: 'Encaja al terminar el traslado desde Barcelona-Sants y dejar el equipaje.',
    practical: 'Mantén el plan corto: la jornada ya incluye AVE, traslado pendiente y check-in.',
    sourceLabel: 'Turisme de Barcelona',
    sourceUrl: 'https://www.barcelonaturisme.com/wv3/en/page/58/gothic-quarter.html',
    eventId: 'ciutat-vella-in',
  },
  {
    id: 'barcelona-sant-pau',
    date: '2026-10-30',
    category: 'essential',
    title: 'Recinte Modernista de Sant Pau',
    description: 'Conjunto modernista conectado visualmente con la Sagrada Família por la Avinguda Gaudí.',
    context: 'Puede funcionar como continuación serena después de la visita guiada de Sagrada Família.',
    practical: 'Confirma disponibilidad y horario; el portal municipal publica la visita libre y la agenda vigente.',
    sourceLabel: 'Ajuntament de Barcelona',
    sourceUrl: 'https://guia.barcelona.cat/ca/directoris/detall/sant-pau-recinte-modernista_92086008873.html',
    eventId: 'sagrada',
  },
  {
    id: 'rome-villa-borghese',
    date: '2026-11-01',
    category: 'essential',
    title: 'Villa Borghese',
    description: 'Gran parque urbano con jardines, fuentes y espacios amplios en el centro de Roma.',
    context: 'Aprovecha el día completo libre en Roma antes de la entrada temprana a los Museos Vaticanos.',
    practical: 'No requiere convertir el día en una carrera de museos; consulta el horario estacional del parque.',
    sourceLabel: 'Turismo Roma',
    sourceUrl: 'https://www.turismoroma.it/it/luoghi/villa-borghese',
  },
  {
    id: 'rome-ponte-santangelo',
    date: '2026-11-02',
    category: 'alternative',
    title: 'Ponte Sant’Angelo y el Tíber',
    description: 'Paseo exterior junto al puente histórico que une el área del Vaticano con Castel Sant’Angelo.',
    context: 'Es una alternativa ligera tras los Museos Vaticanos, sin añadir otra entrada obligatoria.',
    practical: 'Decide en el momento según energía y tiempo real de salida del museo.',
    sourceLabel: 'Turismo Roma',
    sourceUrl: 'https://www.turismoroma.it/en/places/santangelo-bridge',
    eventId: 'vatican',
  },
  {
    id: 'florence-piazzale',
    date: '2026-11-03',
    category: 'alternative',
    title: 'Piazzale Michelangelo',
    description: 'Terraza panorámica con vista del Duomo, Palazzo Vecchio, Arno y Ponte Vecchio.',
    context: 'Déjalo como plan de tarde o atardecer después de la Cúpula; no compite con su acceso puntual.',
    practical: 'Es un mirador exterior: usa calzado cómodo y confirma el traslado de regreso antes de que oscurezca.',
    sourceLabel: 'Feel Florence',
    sourceUrl: 'https://www.feelflorence.it/en/points-interest/piazzale-michelangiolo',
    eventId: 'duomo',
  },
  {
    id: 'florence-pass',
    date: '2026-11-04',
    category: 'practical',
    title: 'Completar el Brunelleschi Pass por proximidad',
    description: 'Agrupa Campanile, Baptisterio, Museo y Santa Reparata sin volver a cruzar el centro innecesariamente.',
    context: 'El propio itinerario reserva este día para completar los monumentos según aperturas y energía.',
    practical: 'Prioriza lo que aún falte del pase; verifica horarios oficiales el mismo día.',
    sourceLabel: 'Agenda del viaje',
    sourceUrl: '#viaje',
    eventId: 'florence-pass',
  },
  {
    id: 'venice-rialto',
    date: '2026-11-05',
    category: 'alternative',
    title: 'Rialto al caer la tarde',
    description: 'Paseo exterior por el puente y el Gran Canal como cierre flexible de la llegada a Venecia.',
    context: 'Sólo si el traslado, consigna y entrada de San Marco se resolvieron sin presión.',
    practical: 'No añadas una visita interior: protege la ventana de 14:00 de San Marco.',
    sourceLabel: 'Agenda del viaje',
    sourceUrl: '#viaje',
  },
  {
    id: 'prague-letna',
    date: '2026-11-07',
    category: 'essential',
    title: 'Parques de Letná',
    description: 'Parque elevado con áreas arboladas y vista panorámica de Praga, apto para paseo y pausa.',
    context: 'Encaja en el principal día completo abierto de Praga.',
    practical: 'Elige el tramo según energía; la información oficial identifica miradores, zonas de descanso y aseos.',
    sourceLabel: 'Prague City Tourism',
    sourceUrl: 'https://prague.eu/en/objevujte/letna-parks-letenske-sady/',
  },
  {
    id: 'paris-latin-quarter',
    date: '2026-11-08',
    category: 'food',
    title: 'Cena y paseo por el Barrio Latino',
    description: 'Barrio histórico junto a Rue Monge, con calles, plazas, cafés y restaurantes para una primera noche sin traslados largos.',
    context: 'Está vinculado al alojamiento de Rue Monge tras llegar desde CDG y completar el check-in.',
    practical: 'Úsalo como plan cercano: evita reservas rígidas tras el vuelo y el traslado.',
    sourceLabel: 'Paris je t’aime',
    sourceUrl: 'https://parisjetaime.com/eng/transport/quartier-latin-p1913',
  },
  {
    id: 'paris-latin-quarter-evening',
    date: '2026-11-09',
    category: 'food',
    title: 'Regreso tranquilo al Barrio Latino',
    description: 'Opción cercana al alojamiento para cenar cuando concluya el programa Louvre–Invalides–Torre Eiffel.',
    context: 'El día ya tiene tres hitos; esta propuesta evita sumar otra visita con horario.',
    practical: 'Reserva sólo si el grupo decide mantener la hora nocturna de la Torre Eiffel.',
    sourceLabel: 'Paris je t’aime',
    sourceUrl: 'https://parisjetaime.com/eng/article/timeless-paris-a897',
  },
];

export function recommendationsForDay(day: TripDay) {
  return discoverRecommendations.filter((recommendation) => recommendation.eventId
    ? day.events.some((event) => event.id === recommendation.eventId)
    : recommendation.date === day.date);
}
